import { getItemDef } from "./sim/items";
import { formatN, formatTempC, formatW } from "./sim/live-stats";
import { itemRadiateCoefW, moduleActiveWasteHeatW, moduleRadiateCoefW } from "./sim/thermal";
import type { Crew, ShipModule, SpaceItem, World } from "./sim/types";
import { getSprite } from "./sprites";
import { makePanelHeaderRef } from "./util/panel";

interface InspectorTarget {
  kind: "module" | "item" | "crew";
  title: string;
  sprite: string | null;
  rows: Array<[string, string]>;
}

export class Inspector {
  private root: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private spriteEl: HTMLDivElement;
  private refresher: ((world: World) => InspectorTarget | null) | null = null;

  constructor() {
    this.root = document.createElement("div");
    this.root.id = "inspector";
    this.root.className = "panel inspector";
    this.root.style.display = "none";

    const { header, title } = makePanelHeaderRef(() => this.hide());
    this.titleEl = title;

    this.spriteEl = document.createElement("div");
    this.spriteEl.className = "inspector-sprite";

    this.bodyEl = document.createElement("div");
    this.bodyEl.className = "panel-body inspector-body";

    this.root.appendChild(header);
    this.root.appendChild(this.spriteEl);
    this.root.appendChild(this.bodyEl);
    document.body.appendChild(this.root);
  }

  hide(): void {
    this.refresher = null;
    this.root.style.display = "none";
  }

  showModule(m: ShipModule): void {
    this.refresher = () => moduleTarget(m);
    this.show(moduleTarget(m));
  }

  showItem(it: SpaceItem): void {
    this.refresher = () => itemTarget(it);
    this.show(itemTarget(it));
  }

  showCrew(world: World, crew: Crew): void {
    this.refresher = (w) => w.crew.has(crew.id) ? crewTarget(w, crew) : null;
    this.show(crewTarget(world, crew));
  }

  refresh(world: World): void {
    if (!this.refresher) return;
    if (this.root.style.display === "none") return;
    const t = this.refresher(world);
    if (!t) { this.hide(); return; }
    this.show(t);
  }

  private show(t: InspectorTarget): void {
    this.titleEl.textContent = t.title;
    if (t.sprite) {
      try {
        this.spriteEl.innerHTML = getSprite(t.sprite).svg;
        this.spriteEl.style.display = "block";
      } catch {
        this.spriteEl.style.display = "none";
      }
    } else {
      this.spriteEl.style.display = "none";
    }
    this.bodyEl.innerHTML = t.rows
      .map(([k, v]) =>
        `<div class="ins-row"><span class="ins-k">${k}</span><span class="ins-v">${v}</span></div>`)
      .join("");
    this.root.style.display = "block";
  }
}

function moduleTarget(m: ShipModule): InspectorTarget {
  const def = m.def;
  const s = def.stats;
  const rows: Array<[string, string]> = [
    ["category",  def.category],
    ["tier",      `${def.tier}`],
    ["footprint", `${def.cells.w} × ${def.cells.h} cells`],
    ["hp",        `${m.hp.toFixed(0)} / ${s.hp}`],
    ["mass",      fmtKg(s.mass)],
    ["tensile",   fmtN(s.tensile)],
    ["crew",      `${s.crew}`],
    ["pwr idle",  fmtW(s.powerIdleW)],
    ["pwr active",fmtW(s.powerActiveW)],
    ["pwr out",   fmtW(s.powerOutW)],
    ["battery",   `${s.batteryWh} Wh`],
    ["heat out",  fmtW(moduleActiveWasteHeatW(def))],
    ["heat rad",  fmtW(moduleRadiateCoefW(def))],
    ["efficiency",`${(s.energyEfficiency * 100).toFixed(0)}%`],
    ["thrm cond", s.thermalConductivity.toFixed(2)],
    ["emissivity",s.thermalEmissivity.toFixed(2)],
    ["surf area", s.surfaceAreaMult === 1 ? "1.0× (cube)" : `${s.surfaceAreaMult.toFixed(1)}× (finned)`],
    ["thrm inert", `${s.thermalInertia} J/kg/K`],
    ["hardness",  s.hardness > 0 ? `${(s.hardness * 100).toFixed(0)}%` : "—"],
    ["temp now",  formatTempC(m.tempK)],
    ["melt pt",   formatTempC(s.meltPointK)],
    ["value/kg",  s.valuePerKg > 0 ? `${s.valuePerKg.toFixed(2)} cr` : "—"],
  ];
  if (def.thrusters && def.thrusters.length > 0) {
    let totalN = 0;
    for (const p of def.thrusters) totalN += p.thrustN;
    rows.push(["nozzles",    `${def.thrusters.length}`]);
    rows.push(["thrust ea",  fmtN(def.thrusters[0].thrustN)]);
    rows.push(["thrust sum", fmtN(totalN)]);
  }
  if (def.weapon) {
    const w = def.weapon;
    rows.push(["class", w.class]);
    if (w.kind === "beam") {
      rows.push(["weapon",  "beam"]);
      rows.push(["damage",  `${w.damage} hp/s`]);
      rows.push(["range",   `${w.range} m`]);
      rows.push(["trigger", w.altFire ? "ALT (shift)" : "FIRE (space)"]);
    } else {
      rows.push(["weapon dmg", `${w.damage}`]);
      rows.push(["rof",        `${w.rof}/s`]);
      rows.push(["speed",      `${w.muzzleVel} m/s`]);
      rows.push(["range",      `${w.range} m`]);
      rows.push(["round mass", fmtKg(w.projectileMass)]);
      rows.push(["trigger",    w.altFire ? "ALT (shift)" : "FIRE (space)"]);
    }
    rows.push(["blast",       w.blastRadiusM > 0 ? `${w.blastRadiusM} m` : "—"]);
    rows.push(["quality mod", `${(w.qualityMod * 100).toFixed(1)}%`]);
    if (w.kind === "beam") {
      const beamW = s.powerActiveW * s.energyEfficiency;
      rows.push(["beam out", fmtW(beamW)]);
    } else {
      rows.push(["heat / hit", w.heatOnImpactJ > 0
        ? `${(w.heatOnImpactJ / 1000).toFixed(1)} kJ`
        : "—"]);
    }
    rows.push(["bounce e", w.restitution > 0 ? w.restitution.toFixed(2) : "—"]);
    rows.push(["spread",   w.spreadRad > 0 ? `${(w.spreadRad * 180 / Math.PI).toFixed(1)}°` : "—"]);
  }
  if (def.mineable) {
    rows.push(["resource", def.mineable.resource]);
    rows.push(["drops",    def.mineable.debrisItemId]);
    rows.push(["quality",  `${(m.quality * 100).toFixed(0)}%`]);
  }
  if (m.cargo && m.cargo.size > 0) {
    let used = 0;
    for (const v of m.cargo.values()) used += v;
    rows.push(["hold used", `${used.toFixed(1)} / ${s.cargoCapacityKg} kg`]);
    for (const [res, kg] of m.cargo) {
      if (kg <= 0) continue;
      rows.push([`  ${res}`, `${kg.toFixed(1)} kg`]);
    }
  } else if (s.cargoCapacityKg > 0) {
    rows.push(["hold used", `0 / ${s.cargoCapacityKg} kg`]);
  }
  if (m.fuelKg != null) {
    rows.push(["fuel", `${m.fuelKg.toFixed(1)} / ${s.fuelCapacityKg} kg`]);
  }
  if (def.reactor && m.fuelBuffer != null) {
    rows.push([
      "hopper",
      `${m.fuelBuffer.toFixed(3)} / ${def.reactor.fuelBufferKg} kg ${def.reactor.fuelResource}`,
    ]);
  }
  if (m.energyWh != null) {
    rows.push(["energy", `${m.energyWh.toFixed(0)} / ${s.batteryWh} Wh`]);
  }
  return { kind: "module", title: def.name, sprite: def.sprite, rows };
}

function crewTarget(world: World, crew: Crew): InspectorTarget {
  const shipName = world.ships.get(crew.shipId)?.name ?? "—";
  let assignment = "idle";
  if (crew.assignedModule) {
    const name = crew.assignedModule.def.name;
    assignment = crew.path.length > 0
      ? `walking to ${name} (${crew.path.length} cell${crew.path.length === 1 ? "" : "s"})`
      : `manning ${name}`;
  }
  const rows: Array<[string, string]> = [
    ["ship", shipName],
    ["cell", `${crew.cellGx}, ${crew.cellGy}`],
    ["status", assignment],
    ["health", `${Math.round(crew.health)} / 100`],
    ["mood", crew.friendliness >= 0
      ? `friendly +${crew.friendliness.toFixed(1)}`
      : `hostile ${crew.friendliness.toFixed(1)}`],
  ];
  return { kind: "crew", title: crew.name, sprite: null, rows };
}

function itemTarget(it: SpaceItem): InspectorTarget {
  const def = getItemDef(it.defId);
  const s = def.stats;
  const rows: Array<[string, string]> = [
    ["category",    def.category],
    ["size",        `${def.sizeM} m`],
    ["hp",          `${it.hp.toFixed(0)} / ${s.hp}`],
    ["mass",        fmtKg(s.mass)],
    ["tensile",     fmtN(s.tensile)],
    ["hardness",    s.hardness > 0 ? `${(s.hardness * 100).toFixed(0)}%` : "—"],
    ["temp now",    formatTempC(it.tempK)],
    ["melt pt",     formatTempC(s.meltPointK)],
    ["thrm cond",   s.thermalConductivity.toFixed(2)],
    ["emissivity",  s.thermalEmissivity.toFixed(2)],
    ["thrm inert",  `${s.thermalInertia} J/kg/K`],
    ["heat rad",    fmtW(itemRadiateCoefW(def))],
    ["resource",    def.resource ?? "—"],
    ["value/kg",    s.valuePerKg > 0 ? `${s.valuePerKg.toFixed(2)} cr` : "—"],
    ["quality",     `${(it.quality * 100).toFixed(0)}%`],
    ["vel",         `${Math.hypot(it.vel.x, it.vel.y).toFixed(2)} m/s`],
  ];
  return { kind: "item", title: def.name, sprite: def.sprite, rows };
}

function fmtKg(kg: number): string {
  if (kg >= 1_000_000) return `${(kg / 1_000_000).toFixed(2)} kt`;
  if (kg >= 1_000) return `${(kg / 1_000).toFixed(2)} t`;
  if (kg >= 1) return `${kg.toFixed(0)} kg`;
  return `${(kg * 1000).toFixed(0)} g`;
}
const fmtW = formatW;
const fmtN = formatN;
